import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig, YAxisConfig} from '../types';

const CARDINAL_TICKS = [0, 90, 180, 270, 360];

/**
 * A bearing's compass point in the user's language. `messages` is read here,
 * inside the function, not at module scope: the mobile tests replace
 * `@smartmixin/i18n` with objects that carry only a few namespaces.
 */
const compassPoint = (
  value: number,
  formatMessage: (descriptor: MessageDescriptor) => string,
): string | null => {
  const angle = Units.getPolarAngleFromDegrees(value);
  return angle === null ? null : formatMessage(messages.compass[angle]);
};

export interface WindDirectionChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  dateTimeConfig?: DateTimeConfig;
  /**
   * Names the compass points on the axis and in the tooltip. Required: the
   * points are `compass.*` messages, and English letters would otherwise
   * reach every locale (French west is O, German east is O).
   */
  formatMessage: (descriptor: MessageDescriptor) => string;
}

export const windDirectionChart = (
  options: WindDirectionChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    granularity,
    units,
    height = 200,
    dateTimeConfig,
    formatMessage,
  } = options;

  const series: SeriesConfig[] = [
    {
      id: 'wind-direction',
      type: 'scatter',
      dataKey: 'DD',
      name: formatMessage(messages.metrics.windDirection),
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'linear',
      showMarkers: true,
      zIndex: 1,
    },
  ];

  const yAxis: YAxisConfig = {
    id: 'direction',
    position: 'left',
    domain: [0, 360],
    allowDecimals: false,
    ticks: CARDINAL_TICKS,
    tickFormat: (value: number) =>
      compassPoint(value, formatMessage) ?? `${value}°`,
    gridLines: true,
    gridColor: theme.colors.grid,
    gridWidth: theme.isDark ? 1 : 0.5,
    labelColor: theme.colors.text,
  };

  const customFormatters: Record<string, (value: number) => string> = {
    'wind-direction': (value: number) => {
      const label = compassPoint(value, formatMessage);
      return label
        ? `${label} (${Math.round(value)}°)`
        : `${Math.round(value)}°`;
    },
  };

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({
      dateTimeConfig,
      type: 'datetime',
      timezone,
      granularity,
      theme,
    }),
    yAxes: [yAxis],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'DD',
      units,
      theme,
      dateTimeConfig,
      customFormatters,
    }),
    legend: createLegendConfig(false, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
